import { Box, Spinner, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  NegotiationImpossibleCase,
  type SourcingEventDto,
  useGetNegotiationWithoutNoticeQuery,
} from "../../../../../../autogen/bff-api";
import { TipTap, isTiptapContentEmpty } from "../../../../../../common/input/TipTap/TipTap";
import { ListLoadError } from "../../../../../../common/ListLoadError";
import { followsDesignContestBelowEeaTier, negotiationCaseProvision } from "../../utils/noCompetition";
import { EmptyValue } from "./SummaryField";

export const NegotiationWithoutNoticeSummary = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const negotiation = publicContext?.negotiationWithoutNotice;
  const { data, isLoading, isError, refetch } = useGetNegotiationWithoutNoticeQuery({ eventId: event.id });

  if (!negotiation?.case) return <EmptyValue text={t("Not set")} />;
  const followsDesignContest = negotiation.case === NegotiationImpossibleCase.DesignContestWinners;
  const precedingCompetition = followsDesignContest
    ? undefined
    : data?.precedingCompetitions.find((c) => c.eventId === negotiation.precedingEventId);
  const precedingContest = followsDesignContest
    ? data?.designContests?.find((c) => c.eventId === negotiation.precedingEventId)
    : undefined;
  const invitees = data?.invitees ?? [];

  return (
    <Box>
      <Text>{t(negotiation.case)}</Text>
      <Text fontSize="xs" color="smMuted" fontStyle="italic">
        {negotiationCaseProvision(
          negotiation.case,
          publicContext?.regulation,
          t,
          followsDesignContestBelowEeaTier(publicContext),
        )}
      </Text>
      {negotiation.rationale && !isTiptapContentEmpty(negotiation.rationale) && (
        <Box mt="1" fontSize="sm" color="smMuted">
          <TipTap content={negotiation.rationale} editable={false} hasBorder={false} padding="0" maxHeight="" />
        </Box>
      )}
      {precedingCompetition && (
        <Text fontSize="sm" mt="2">
          {t("Follows the failed competition {{title}}", { title: precedingCompetition.title })}
        </Text>
      )}
      {precedingContest && (
        <Text fontSize="sm" mt="2">
          {t("Follows the design contest {{title}}", { title: precedingContest.title })}
        </Text>
      )}
      {isLoading ? (
        <Spinner size="sm" mt="2" />
      ) : isError || !data ? (
        <Box mt="2">
          <ListLoadError message={t("Could not load the invited suppliers.")} onRetry={refetch} />
        </Box>
      ) : (
        <Text fontSize="sm" mt="2">
          {invitees.length > 0
            ? t("Invites {{suppliers}}", { suppliers: invitees.map((supplier) => supplier.name).join(", ") })
            : t("No suppliers invited yet")}
        </Text>
      )}
    </Box>
  );
};
